import React, { useState, useEffect, useRef, useCallback } from "react";
import _ from "lodash";
import classnames from "classnames/bind";
import style from "./index.module.scss";
import filterProductByName from "../../../../utils/filterProductByName";
import underline from "../../../../utils/underline";
import instance from "../../../../services/httpRequest";
import { ISearchProduct as IProduct, Replacement } from "../../../../interface";
import Loading from "../../../Loading";
import oilThumb from "../../../../images/oil-thumb-placeholder.png";
import paginatorConfig from "../../../../config/paginatorConfig";
import localesReplacement from "../../../../utils/localesReplacement";
import DOMPurify from "dompurify";
import ClickOut from "react-click-out";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import actions from "../../../../redux/actions";
import { useMediaQuery } from "react-responsive";
import { useAppName } from "../../../../hooks/use-app-names";
import { useAnalytics } from "../../../../hooks/use-analytics";
import {
  APPNAME,
  FASTSCAN,
  ANALYTICS_EVENT,
  ANALYTICS_PAGE_TYPE,
  ANALYTICS_SEARCH_TYPE,
  ANALYTICS_USER_TYPE,
} from "../../../../constants";

import {
  useSearchParams,
  useLocation,
  useNavigationType,
} from "react-router-dom";

const cx = classnames.bind(style);

interface IProps {
  replacements: Replacement;
  onProductSelect?: (value: string | number, product: string) => void;
  selectedWorkshop: boolean;
  restore?: boolean;
  preserved?: boolean;
  setFilterResults: (v: IProduct[] | null) => void;
  setLoading: (v: boolean) => void;
  setLicenseKeys: (v: string) => void;
  country?: string;
}

export const OilSearch = (props: IProps) => {
  const {
    replacements,
    onProductSelect,
    selectedWorkshop,
    restore,
    preserved,
    setFilterResults,
    setLoading,
    setLicenseKeys,
    country,
  } = props;
  const isDesktopOrLaptop = useMediaQuery({ minWidth: 1024 });
  const isFastScan = useAppName(FASTSCAN);
  const sendAnalytics = useAnalytics();
  const [searchParams, setSearchParams] = useSearchParams();
  const ref = useRef<NodeJS.Timeout | null>(null);
  const [products, setProducts] = useState<IProduct[]>([]);
  const [value, setValue] = useState<string>("");
  const [mPageIndex, setMPageIndex] = useState<number>(0);
  const [isMount, setIsMount] = useState<boolean>(false);
  const [finish, setFinish] = useState<boolean>(false);
  const limit = paginatorConfig.LIMIT;
  const controller = new AbortController();
  const location = useLocation();
  const navigationType = useNavigationType();

  useEffect(() => {
    if (navigationType === "POP") {
      if (value.endsWith(" ")) {
        setValue(value.trim());
      } else {
        setValue(`${value} `);
      }
    }
  }, [location]);

  const memoDate = useCallback(() => {
    setFinish(false);
    selectedWorkshop
      ? _.assign(instance.defaults.headers, { isWorkshopOwner: selectedWorkshop })
      : _.unset(instance.defaults.headers, "isWorkshopOwner");
    instance
      .get("/products/all", {
        signal: controller.signal,
      })
      .then((d) => {
        setFinish(true);
        d && setProducts(d.data);
      })
      .catch(() => _.noop());
  }, [selectedWorkshop]);

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);
  const mobileProducts = _.take(
    filterProductByName(products, value),
    limit * (mPageIndex + 1)
  );
  const onChangeHandler = (value: string) => {
    setValue(value);
    restore && sessionStorage.setItem("searchOKey", value);
    if (value.length >= 2) {
      setIsMount(true);
      setLoading(true);
      setVisible(true);
      if (isDesktopOrLaptop) {
        searchParams.delete("id");
        searchParams.set("page", "landing");
        isFastScan && searchParams.set(APPNAME, FASTSCAN);
        setSearchParams(searchParams);
      }
      ref.current && clearTimeout(ref.current);
      ref.current = setTimeout(() => {
        products && setIsMount(false);
        products && setLoading(false);

        sendAnalytics({
          event: filterProductByName(products, value)
            ? ANALYTICS_EVENT.OIL_SEARCH
            : ANALYTICS_EVENT.OIL_SEARCH_UNAVAILABLE,
          page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
          query: value,
          search: ANALYTICS_SEARCH_TYPE.OIL_SEARCH,
          user: selectedWorkshop
            ? ANALYTICS_USER_TYPE.WORKSHOP_OWNER
            : ANALYTICS_USER_TYPE.VEHICLE_OWNER,
        });
      }, 1000);
    }
  };
  const showMore = () => {
    setMPageIndex(mPageIndex + 1);
  };

  useEffect(() => {
    memoDate();
    setValue(value);
    onChangeHandler(value);
  }, [selectedWorkshop, country]);

  useEffect(() => {
    if (preserved) {
      setValue(sessionStorage.getItem("searchOKey") ?? "");
      if (!_.isEmpty(products)) {
        setIsMount(true);
        setLoading(true);
        setTimeout(() => {
          setIsMount(false);
          setLoading(false);
        }, 1000);
      }
    }
  }, [preserved, products]);

  const [visible, setVisible] = useState(false);

  const itemClickHandler = (product: IProduct) => {
    setVisible(false);
    onProductSelect && onProductSelect(product["id"], product["name"]);
  };

  useEffect(() => {
    if (!isMount) {
      if (_.trim(value).length >= 2) {
        if (_.isEmpty(filterProductByName(products, value))) {
          setFilterResults(null);
          setLicenseKeys(`${localesReplacement(
            replacements,
            "No results found for"
          )}
          <strong>${_.trim(value)}</strong>`);
        } else {
          setFilterResults(filterProductByName(products, value));
        }
      } else {
        setFilterResults(null);
        setLicenseKeys("");
      }
    } else {
      setFilterResults([]);
    }
  }, [products, value, isMount]);
  // baltic region reload page
  useEffect(() => {
    const shouldReload =
      location.pathname.includes("/baltic-region/") &&
      location.pathname.includes("/wtb-site-tunnel/") &&
      !sessionStorage.getItem("oilSearchReloaded");

    if (shouldReload) {
      sessionStorage.setItem("oilSearchReloaded", "true");
      window.location.reload();
    }
  }, [location.pathname]);

  return (
    <>
      <div className={cx("label")}>
        {localesReplacement(replacements, "FLUID & OIL Search")}
      </div>
      <ClickOut className="click-out" onClickOut={() => setVisible(false)}>
        <div className={cx("wrapper")}>
          <div className={cx("search-input", { withValue: value })}>
            <input
              type="text"
              value={value}
              onChange={(ev) => onChangeHandler(ev.target.value)}
              placeholder={localesReplacement(
                replacements,
                "Search by the oil name"
              )}
              onFocus={() => {
                if (_.trim(value).length >= 2) {
                  setVisible(true);
                }
              }}
            />
            {value.length ? (
              <i
                className={cx("remove")}
                onClick={() => {
                  setValue("");
                  restore && sessionStorage.removeItem("searchOKey");
                }}
              ></i>
            ) : (
              <i className={cx("voice")}></i>
            )}
          </div>
          {visible && (
            <div className={cx("flyout")}>
              {!isMount && finish ? (
                <>
                  {!_.isEmpty(filterProductByName(products, value)) ? (
                    <div
                      className={cx("result", {
                        "with-paginator":
                          filterProductByName(products, value) &&
                          Math.ceil(
                            filterProductByName(products, value).length / limit
                          ) > 1,
                      })}
                    >
                      <p className={cx("header")}>
                        {products.length} product(s) found.
                      </p>
                      {_.map(mobileProducts, (product, index) => (
                        <div
                          key={index}
                          className={cx(
                            "result-row",
                            "mobile",
                            "product-trigger"
                          )}
                          onClick={() => {
                            itemClickHandler(product);
                          }}
                        >
                          <ul>
                            <li>
                              <img
                                src={
                                  typeof product['packartUrl'] === 'string'
                                    ? product['packartUrl'] || oilThumb
                                    : Array.isArray(product['packartUrl'])
                                      ? product['packartUrl'][0] || oilThumb
                                      : oilThumb
                                }
                              />
                            </li>
                            <li
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(
                                  underline(product["name"], value)
                                ),
                              }}
                            ></li>
                            <li>
                              <button className={cx("recommonended-btn")}>
                                {localesReplacement(
                                  replacements,
                                  "View product page"
                                )}
                              </button>
                            </li>
                          </ul>
                        </div>
                      ))}
                      {!!filterProductByName(products, value) &&
                        mobileProducts.length <
                        filterProductByName(products, value).length && (
                          <div className={cx("btn-show-more")}>
                            <span onClick={showMore}>
                              {localesReplacement(replacements, "Show more")}
                            </span>
                          </div>
                        )}
                    </div>
                  ) : (
                    _.trim(value).length >= 2 && (
                      <div className="no-match">
                        <div className="no-found-text no-oil-text">
                          <p>
                            {localesReplacement(
                              replacements,
                              "No results found for"
                            )}
                            <strong>{_.trim(value)}</strong>
                          </p>
                        </div>
                      </div>
                    )
                  )}
                </>
              ) : (
                <Loading />
              )}
            </div>
          )}
        </div>
      </ClickOut>
    </>
  );
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setFilterResults: (vehicle: IProduct[] | null) =>
    dispatch(actions.setResults(vehicle)),
  setLoading: (v: boolean) => dispatch(actions.setLoading(v)),
  setLicenseKeys: (v: string) => dispatch(actions.setLicenseKeys(v)),
});
export default connect(null, mapDispatchToProps)(OilSearch);
